import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import {
  HistoryGlyph,
  PlusIcon,
  ToolbarPlusIcon,
  TrashIcon,
  TriangleAlertIcon,
} from "../labInvestigations/labIcons";
import AllergyService from "./AllergyService";
// Upstream promoted the confirm dialog to a shared component (also used by
// Physical activity); it now carries the 3410:2786 board styling, so the
// section-local copy is gone rather than duplicated.
import DeleteConfirmDialog from "../common/DeleteConfirmDialog";
import DeletedEntriesModal from "./DeletedEntriesModal";
import TrendModal from "./TrendModal";
import MetaTooltip from "../medicines/MetaTooltip";
import { ALG_V2_ADD, ALG_V2_EDIT, ALG_V2_EDIT_CAPTURED, ALG_V2_REFRESH } from "./events";

/**
 * Allergies — content column, backed by api-allergy-v2-service. Px-matched to
 * "Card · Allergies (saved)" (3071:3129, 1024×414). Header CTA opens the ADD
 * form in the editor rail (ALG_V2_ADD → AllergyEditor); a row-name click
 * opens the UPDATE form in the same rail (ALG_V2_EDIT, carrying the record) —
 * this column no longer owns any edit state. Per-row delete is the board's red
 * Material trash, confirmed through the shared DeleteConfirmDialog, which
 * carries the 3410:2786 board styling. No status chip, no trend, no
 * criticality flag.
 */

// Table columns: Allergen · Category · First noted (year) · Reaction (chip
// list) · Last reaction (date) · Description (1-line ellipsis) · trash.

const yearOf = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : String(d.getUTCFullYear());
};

// Build a synthetic Allergy-shaped row from a staged "add" captured item
// so it can slot into the same table as Allergy v2 rows. Mirrors the
// other Wave A sections. Wave B first-cut: staged UPDATES aren't
// rendered.
const stagedAddToAllergyRow = (item) => {
  const p = (item && item.payload) || {};
  // Reactions arrive as either a comma-separated string (from the AI
  // agent's `add_allergy`) or a list of {name} objects (from the manual
  // card). Normalise into the {name} shape the table renders from.
  // (The earlier per-reaction `source` field was retired in 2026-09.)
  let reactions = [];
  if (Array.isArray(p.reactions)) {
    reactions = p.reactions
      .map((r) => (r && typeof r === "object" ? { name: r.name } : { name: String(r || "") }))
      .filter((r) => r.name && r.name.trim());
  } else if (typeof p.reactions === "string" && p.reactions.trim()) {
    reactions = p.reactions.split(",")
      .map((n) => n.trim())
      .filter(Boolean)
      .map((name) => ({ name }));
  }
  return {
    allergyId:    `staged-${item.id}`,
    name:         p.substance || p.name || p.value || "(untitled)",
    category:     p.category || "Other",
    reactions,
    firstNoted:   p.since_date || null,
    lastReaction: null,
    description:  p.description || "",
    _captured:     item,
    _capturedIsAi: (item.confidence || 0) > 0,
    _stagedAdd:    true,
  };
};

const Allergies = ({ patient, capturedItems, onRowsSync }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // allergy object → confirm dialog
  const [deletingId, setDeletingId] = useState(null);
  const [trendTarget, setTrendTarget] = useState(null); // allergy object → trend modal
  const [showDeletedModal, setShowDeletedModal] = useState(false);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new AllergyService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { allergies: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load allergies");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(ALG_V2_REFRESH, load);
    return () => EventService.removeListener(ALG_V2_REFRESH, load);
  }, [load]);

  const rowsRaw = (doc && doc.allergies) || [];

  // Bubble the current base v2 list up to the transcript modal so it
  // can dedup AI proposals against existing rows (proposals whose
  // name matches an existing entry render as an update card on that
  // record instead of a duplicate add card).
  useEffect(() => {
    if (typeof onRowsSync === "function") onRowsSync(rowsRaw);
  }, [rowsRaw, onRowsSync]);

  // Transcript-mode overlay. Adds slot in as new peach rows; updates
  // annotate the matching existing row with _captured so the reviewer
  // sees the peach highlight on the row they just modified.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const rows = useMemo(() => {
    if (!inTranscriptMode) return rowsRaw;
    const stagedAdds = [];
    const updatesByEntryId = new Map();
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      if (it.operation === "add") stagedAdds.push(stagedAddToAllergyRow(it));
      else if (it.operation === "update" && it.targetEntryId) {
        updatesByEntryId.set(String(it.targetEntryId), it);
      }
    }
    // Overlay staged payload fields onto the base row so the table
    // columns (Name / Category / Reactions / First noted / Description)
    // show what the reviewer just saved, not the untouched Allergy v2
    // record.
    const annotated = rowsRaw.map((row) => {
      const update = updatesByEntryId.get(String(row.allergyId));
      if (!update) return row;
      const p = (update.payload) || {};
      return {
        ...row,
        name:        p.name || p.substance || row.name,
        category:    p.category || row.category,
        reactions:   Array.isArray(p.reactions) ? p.reactions : row.reactions,
        firstNoted:  p.since_date || row.firstNoted,
        description: p.description !== undefined ? p.description : row.description,
        _captured:     update,
        _capturedIsAi: (update.confidence || 0) > 0,
      };
    });
    return [...annotated, ...stagedAdds];
  }, [rowsRaw, capturedItems, inTranscriptMode]);
  // Soft-deleted allergies ride along on the same GET payload. The toolbar
  // "Deleted entries" button lights up when this list is non-empty.
  const deletedRows = useMemo(
    () => (doc && Array.isArray(doc.deletedAllergies) ? doc.deletedAllergies : []),
    [doc]
  );

  const openAdd = () => EventService.emit(ALG_V2_ADD);
  // The rail prefills from the record itself — no local edit state here.
  // Transcript-mode captured rows route to a re-edit event so the modal
  // can pop the AI proposal back open as an editable card rather than
  // opening the standalone edit sheet in the patient-page rail.
  const openEdit = (a) => {
    if (a && a._captured) {
      EventService.emit(ALG_V2_EDIT_CAPTURED, {
        captured: a._captured,
        algv2Record: a._stagedAdd ? null : a,
      });
      return;
    }
    EventService.emit(ALG_V2_EDIT, { allergy: a });
  };

  // Trash → the board's confirm dialog (3410:2786); the wire call runs on
  // its "Delete entry".
  const confirmDelete = () => {
    const a = deleteTarget;
    if (!a) return;
    setDeletingId(a.allergyId);
    const svc = new AllergyService();
    svc.delete(
      patientId,
      a.allergyId,
      (doc && doc.version) || 0,
      (res) => {
        setDoc(res || doc);
        setDeletingId(null);
        setDeleteTarget(null);
        EventService.emit(ALG_V2_REFRESH);
      },
      (err) => {
        setDeletingId(null);
        setDeleteTarget(null);
        if (err && err.reason === "version") load();
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Delete failed — please retry");
      }
    );
  };

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading allergies…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="cc3-card al3">
      <header className="cc3-toolbar">
        <h2 className="cc3-title">Allergies</h2>
        {rows.length === 0 ? (
          <span className="cc3-count is-none">&middot; No entries</span>
        ) : (
          <span className="cc3-count">
            &middot; {rows.length} {rows.length === 1 ? "entry" : "entries"}
          </span>
        )}
        {/* The Deleted-entries button is independent of the live list —
            deleting the LAST allergy shouldn't strand the audit trail
            behind. The Add-allergy toolbar CTA is still hidden on empty
            (3071:3720) — the body button is the only add affordance
            until the first entry exists — but "View deleted" stays as
            long as there IS a deleted row on the current doc. */}
        {(rows.length > 0 || deletedRows.length > 0) && (
          <div className="al3-actions">
            {/* Only shown when the current doc has any soft-deleted rows.
                Payload arrives on the same GET; no extra fetch here. */}
            {deletedRows.length > 0 && (
              <button
                type="button"
                className="fh3-btn fh3-btn-deleted"
                onClick={() => setShowDeletedModal(true)}
                title="View deleted allergies"
              >
                <HistoryGlyph /> Deleted entries
                <span className="fh3-btn-count"> · {deletedRows.length}</span>
              </button>
            )}
            {rows.length > 0 && (
              <button type="button" className="cc3-add al3-add" onClick={openAdd}>
                <ToolbarPlusIcon /> Add allergy
              </button>
            )}
          </div>
        )}
      </header>

      {rows.length === 0 ? (
        <>
          {/* Empty board keeps a thead — the STANDARD grey 38h one (its
              own 6-col grid), not the populated board's white 50h header.
              Analysis column mirrors the populated board. */}
          <div className="al3-thead is-empty" role="row">
            <span role="columnheader">Allergen</span>
            <span role="columnheader">Category</span>
            <span role="columnheader">First noted</span>
            <span role="columnheader">Reaction</span>
            <span role="columnheader">Last reaction</span>
            <span role="columnheader">Description</span>
            <span role="columnheader">Analysis</span>
          </div>
          <div className="al3-empty">
            <span className="al3-empty-medallion" aria-hidden="true"><TriangleAlertIcon /></span>
            <p className="al3-empty-title">No allergies recorded</p>
            <p className="al3-empty-hint">
              Nothing has been logged yet. Add the patient&rsquo;s first allergy to begin building their history.
            </p>
            <button type="button" className="al3-empty-cta" onClick={openAdd}>
              <PlusIcon /> Add allergy
            </button>
          </div>
        </>
      ) : (
        <div className="al3-table" role="table">
          <div className="al3-thead" role="row">
            <span role="columnheader">Allergen</span>
            <span role="columnheader">Category</span>
            <span role="columnheader">First noted</span>
            <span role="columnheader">Reaction</span>
            <span role="columnheader">Last reaction</span>
            <span role="columnheader">Description</span>
            <span role="columnheader">Analysis</span>
            <span role="columnheader" aria-label="Actions" />
          </div>

          {rows.map((a) => (
            <div
              className={`al3-row${a._captured ? " is-captured" : ""}`}
              role="row"
              key={a.allergyId}
            >
              <span className="al3-cell" role="cell">
                {/* Captured (peach) rows stay clickable — openEdit
                    branches on _captured and routes to
                    ALG_V2_EDIT_CAPTURED so the modal can pop the AI
                    proposal back open as an editable card. */}
                <button
                  type="button"
                  className="al3-name-btn"
                  onClick={() => openEdit(a)}
                  title={a._stagedAdd ? "Edit captured allergy" : "Edit allergy"}
                >
                  {a._capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
                  {a.name}
                </button>
              </span>
              <span className="al3-cell" role="cell">{a.category || "—"}</span>
              <span className="al3-cell" role="cell">{yearOf(a.firstNoted)}</span>
              <span className="al3-cell al3-c-rx" role="cell">
                {(a.reactions || []).length === 0 ? (
                  <span className="cc-dash">—</span>
                ) : (
                  (a.reactions || []).map((r) => (
                    // Reaction chip wraps its own long text (see the
                    // `overflow-wrap: anywhere` on `.al3-rx`), so no
                    // tooltip is needed — the label is always fully
                    // visible inside the chip.
                    <span key={r.name} className="al3-rx">{r.name}</span>
                  ))
                )}
              </span>
              <span className="al3-cell al3-c-last" role="cell">
                {a.lastReaction ? formatShortDate(a.lastReaction) : <span className="cc-dash">—</span>}
              </span>
              <span className="al3-cell al3-c-desc" role="cell">
                {/* 3-line clamp + white hover tooltip; only reveals
                    when the text is actually being truncated. */}
                <MetaTooltip text={a.description || ""} lines={3} />
              </span>
              <span className="al3-cell al3-c-analysis" role="cell">
                {a._stagedAdd ? (
                  <span className="cc-dash">—</span>
                ) : (
                  <button
                    type="button"
                    className="al3-trend-link"
                    onClick={() => setTrendTarget(a)}
                    title="View audit history"
                  >
                    View trend
                  </button>
                )}
              </span>
              <span className="al3-cell al3-c-actions" role="cell">
                {!a._stagedAdd && (
                  <button
                    type="button"
                    className="al3-del"
                    onClick={() => setDeleteTarget(a)}
                    disabled={deletingId === a.allergyId}
                    aria-label={`Delete ${a.name}`}
                    title="Delete"
                  >
                    <TrashIcon />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          name={deleteTarget.name}
          listLabel="allergies"
          submitting={deletingId === deleteTarget.allergyId}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}

      {showDeletedModal && (
        <DeletedEntriesModal
          patient={patient}
          entries={deletedRows}
          onClose={() => setShowDeletedModal(false)}
        />
      )}

      {trendTarget && (
        <TrendModal
          patientId={patientId}
          allergyId={trendTarget.allergyId}
          allergyName={trendTarget.name}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

Allergies.propTypes = {
  patient: PropTypes.object,
  capturedItems: PropTypes.array,
  /** Fires whenever the base v2 allergy list changes. Transcript
   *  modal uses it to dedup AI proposals against existing rows. */
  onRowsSync: PropTypes.func,
};

export default Allergies;
